import { ExclamationCircleOutlined } from '@ant-design/icons';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, Form, Modal, Space, Spin } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import OtpInput from 'react-otp-input-rc-17';
import { AuthApi } from '../../../apis/auth-api';
import { LoginCookie } from '../../../models/login-cookie-interface';
import { CSPORTAL_APPLICATION_ID } from '../../../utilites/api';
import { AuthContext } from '../../../utilites/auth';
import { Logger } from '../../../utilites/loglySetup';
const { confirm } = Modal;

interface LoginFormFields {
  UserName: string;
  Password: string;
  AuthenticationCode: string;
  RememberMe: boolean;
  TrustThisDevice: boolean;
}

type Props = {
  twoFAProps: { maskedNumber: string, daysToRememberDevice: string },
  isModalVisible: boolean,
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>,
  handleSubmit: (data: LoginFormFields) => {},
  loginFields: LoginFormFields
  setIsMainLoading: React.Dispatch<React.SetStateAction<boolean>>,
  is2FAModalLoading: boolean,
  setIs2FAModalLoading: React.Dispatch<React.SetStateAction<boolean>>
};

const TwoFAModal: React.FC<Props> = ({
  twoFAProps,
  isModalVisible,
  setIsModalVisible,
  handleSubmit,
  loginFields,
  setIsMainLoading,
  is2FAModalLoading,
  setIs2FAModalLoading
}) => {
  const [isBtnDisabled, setIsBtnDisabled] = useState<boolean>(true);
  const [form] = Form.useForm();
  const { profile } = React.useContext(AuthContext);
  /**
   * Cancel 2fa form and return to login form
   */
  const handleCancel = () => {
    setIsMainLoading(false);
    setIsModalVisible(false);
    form.resetFields();
  };

  /**
   * Initial submission of 2FA form used for validation
   */
  const handleOk = async () => {
    try {
      await form.validateFields();
      setIs2FAModalLoading(true);
    } catch (errorInfo) {
      Logger().log("ErrorInfo: ", errorInfo);
    }
  };

  /**
   * Submit 2fa form
   * @param values 
   */
  const onFinish = async (values: { code: string, trustThisDevice: boolean }) => {
    const authCode = values.code;
    const data: LoginFormFields = {
      UserName: loginFields.UserName,
      Password: loginFields.Password,
      AuthenticationCode: authCode,
      RememberMe: loginFields.RememberMe,
      TrustThisDevice: values.trustThisDevice
    };
    handleSubmit(data);
  };

  /**
   * Validation for code input
   * @param _ 
   * @param value 
   * @returns promise
   */
  const checkCode = (_: any, value: string) => {
    const reg = /^\d$/;
    if (!isNaN(parseInt(value)) && reg.test(value)) {
      return Promise.resolve();
    }

    return Promise.reject(new Error(''));
  }

  /**
   * Resent 2fa code
   */
  const resend2FACode = async () => {
    setIs2FAModalLoading(true);

    const loginCookie: LoginCookie = {
      username: loginFields.UserName,
      password: loginFields.Password,
      authenticationCode: null,
      deviceToken: null,
      applicationId: CSPORTAL_APPLICATION_ID,
    }

    try {
      await AuthApi.postLogin(loginCookie);
      Modal.success({
        title: 'Resent Code',
        content: 'A new 6 digit code was sent to your mobile number.',
        centered: true
      });
    } catch (e) {
      Logger().error(e);
    }

    setIs2FAModalLoading(false);
  }

  /**
   * Show message when user clicks on problems link
   */
  const handleProblems = () => {
    confirm({
      title: 'Code Problems',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Did not receive your code? You can choose to send another code.</p>
          <p>If you continue to experience issues receiving or verifying your code, contact your Govenda administrator, and they can generate a one-time code you can use to sign in.</p>
        </div>
      ),
      cancelText: 'Dismiss',
      okText: 'Resend Code',
      centered: true,
      onOk() {
        resend2FACode();
      }
    });
  }

  /**
   * Show more information on trusting device
   */
  const handleQuestionIcon = () => {
    Modal.info({
      title: 'Trust This Device',
      content: (
        <div>
          <p>You can trust this device so you do not need to enter an authentication code on every sign in.</p>
          <p>Your organization has set {twoFAProps?.daysToRememberDevice === "-1" ? 'no': `a {twoFAProps.daysToRememberDevice} day`} limit on how long a device can be trusted.</p>
        </div>
      ),
      centered: true
    })
  }

  return (
    <Modal
      className="two-factor-modal"
      centered
      title="Two-Factor Authentication"
      visible={isModalVisible}
      onCancel={handleCancel}
      onOk={handleOk}
      getContainer={false}
      footer={[
        <Button id="Cancel2FAButton" key="back" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button id="Login2FAButton" form="twoFAForm" key="submit" htmlType="submit" type="primary" loading={is2FAModalLoading} onClick={handleOk} disabled={isBtnDisabled}>
          Sign In
        </Button>
      ]}
    >
      <Spin spinning={is2FAModalLoading}>
        <p>For enhanced account security, your organization uses two-factor authentication.</p>
        <p>A text message with a verification code has been sent to <strong>{twoFAProps?.maskedNumber}</strong>.</p>
        <p>Enter the verification code below to sign in.</p>
        <Button id="Problem2FAButton" type="link" onClick={handleProblems}>Problems with your code?</Button>
        <Form
          layout="inline"
          form={form}
          id="twoFAForm"
          onFinish={onFinish}
          initialValues={{
            trustThisDevice: false,
          }}
        >
          <Form.Item name="code" className="code-input">
            <OtpInput
              onChange={(otp: string) => setIsBtnDisabled(otp?.length < 6)}
              numInputs={6}
              isInputNum
            />
          </Form.Item>
          {twoFAProps?.daysToRememberDevice !== "0" &&
            <Form.Item>
              <Space>
                <Form.Item name="trustThisDevice" valuePropName="checked" noStyle>
                  <Checkbox>Trust this device for{twoFAProps?.daysToRememberDevice === "-1" ? 'ever': ` ${twoFAProps?.daysToRememberDevice} days`}</Checkbox>
                </Form.Item>
                <FontAwesomeIcon icon={faQuestionCircle} onClick={handleQuestionIcon} />
              </Space>
            </Form.Item>
          }
        </Form>
      </Spin>
    </Modal>
  )
}

export default TwoFAModal;